import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import SubmitIcon from "@material-ui/icons/Save";
import { httpClient } from "api/httpClient";
import { CustomFieldWarningColumn } from "components/CustomFields/CustomFieldWarningColumn";
import {
	DateFormatField,
	DateTimeField,
	DateTimeFormatField,
	TimeFormatField,
} from "components/fields/DateFormatField";
import { EditableGridCellField } from "components/fields/EditableGridCellField";
import { PhoneField } from "components/fields/PhoneField";
import { SelectEnumInput } from "components/inputs/SelectEnumInput";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import { envSettings } from "core/EnvSettings";
import { OrdersDetailsType } from "entities/OrdersDetailsType";
import { SplitOrdersType } from "entities/SplitOrdersType";
import { keyBy } from "lodash";
import React, { useEffect, useState } from "react";
import {
	Button,
	Confirm,
	NumberField,
	NumberInput,
	ReferenceField,
	ReferenceInput,
	SelectField,
	SelectInput,
	TextField,
	TopToolbar,
	sanitizeListRestProps,
	useRefresh,
	useTranslate,
} from "react-admin";
import { isNullUndEmpty } from "utils/general";
import { getOrderServiceActiveChoices } from "utils/getter";
import { isEmptyObject } from "utils/validation/required";

import {
	DateInput,
	DateTimeInput,
	PhoneInput,
	TimeInput,
	YesNoInput,
} from "../../../components/inputs";
import { HideableOrderField } from "../../../entities/HideableOrderField";
import { useTerminalFieldVisibility } from "../../../hooks/useTerminalFieldVisibility";
import { useNotifyError } from "../../../utils/notifiers/useNotifyError";
import { useNotifySuccess } from "../../../utils/notifiers/useNotifySuccess";
import { FakeReferenceTextField } from "../../fields/FakeReferenceTextField";
import { SelectEnumField } from "../../fields/SelectEnumField";
import { YesNoField } from "../../fields/YesNoField";
import { WarningField, WarningList } from "../../HighlightList";
import { ToolbarButton } from "../ExportDialog/ToolbarButton";

const isDetailsContainError = (details) => {
	const detailsValues = Object.values(details);
	return detailsValues.some((item) => item.type === OrdersDetailsType.error);
};

const allowReplaceSource = (record, source, replaceSource) =>
	!isNullUndEmpty(record[replaceSource]);

const cleanWarningFields = (orderData) => {
	const cleanedOrder = JSON.parse(JSON.stringify(orderData));
	if (!cleanedOrder.details_info || typeof cleanedOrder.details_info !== "object") {
		return cleanedOrder;
	}

	Object.keys(cleanedOrder.details_info).forEach((fieldKey) => {
		const detailInfo = cleanedOrder.details_info[fieldKey];
		if (detailInfo && detailInfo.type === OrdersDetailsType.warning) {
			if (cleanedOrder.custom_fields && Array.isArray(cleanedOrder.custom_fields)) {
				const customFieldIndex = cleanedOrder.custom_fields.findIndex(
					(field) => field.key === fieldKey
				);

				if (customFieldIndex !== -1) {
					cleanedOrder.custom_fields[customFieldIndex].value = null;
				}
			}

			if (cleanedOrder.hasOwnProperty(fieldKey)) {
				cleanedOrder[fieldKey] = null;
			}
		}
	});

	return cleanedOrder;
};

const PreviewDialog = ({ onDialogClose, sharedData, setSharedData }) => {
	const t = useTranslate();
	const refresh = useRefresh();
	const notifyError = useNotifyError();
	const notifySuccess = useNotifySuccess();

	const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
	const [createLoading, setCreateLoading] = useState(false);
	const [submitDisabled, setSubmitDisabled] = useState(false);

	const ordersWithWarningsData = sharedData?.data || [];
	const setSharedDataPreviewData = (data) => setSharedData({ ...sharedData, data: data });
	const onlyOrdersWithDetails = ordersWithWarningsData.filter((item) =>
		isDetailsContainError(item.details_info)
	);
	const allowSubmit = onlyOrdersWithDetails.length < ordersWithWarningsData.length;
	const allowConfirmation = allowSubmit && onlyOrdersWithDetails.length > 0;
	var split_orders_type =
		sharedData?.split_orders_type !== undefined
			? sharedData?.split_orders_type
			: SplitOrdersType.none;
	const extraProps = {
		mapper_type: sharedData?.mapper_type || null,
		order_import_id: sharedData?.order_import_id || null,
		company_id: sharedData?.company_id || null,
		split_orders_type: split_orders_type,
		add_loading_sequence_to_ref: sharedData?.add_loading_sequence_to_ref || false,
		attach_source_doc: sharedData?.attach_source_doc || false,
		split_document_content: sharedData?.split_document_content || false,
		should_generate_pallets: sharedData?.should_generate_pallets || false,
		setSubmitDisabled,
		setSharedDataPreviewData,
		isExistWarnings: onlyOrdersWithDetails.length > 0,
	};

	useEffect(() => {
		setSubmitDisabled(!allowSubmit);
	}, [allowSubmit]);

	const orderCreate = () => {
		const ordersWithCleanWarningsData = ordersWithWarningsData.map((order) =>
			cleanWarningFields(order)
		);
		const _data = {
			order_import_id: extraProps.order_import_id,
			attach_source_doc: extraProps.attach_source_doc,
			split_document_content: extraProps.split_document_content,
			should_generate_pallets: extraProps.should_generate_pallets,
			split_orders_type: extraProps.split_orders_type,
			order_items: ordersWithCleanWarningsData.filter(
				(item) => !isDetailsContainError(item.details_info)
			),
		};
		setCreateLoading(true);
		httpClient("orders-upload-save", { method: "POST", body: JSON.stringify(_data) })
			.then(() => {
				notifySuccess(t("osfc.dialogs.orders-import-preview.msg.success"));
				setSharedData({});
				onDialogClose();
				refresh();
			})
			.catch((error) => {
				notifyError(error);
			})
			.finally(() => {
				setCreateLoading(false);
			});
	};

	const handleConfirmOnClose = () => setSubmitConfirmOpen(false);
	const handleConfirmOnOpen = () => setSubmitConfirmOpen(true);

	const handleOnSubmitConfirm = () => {
		handleConfirmOnClose();
		orderCreate();
	};

	const handleOnSubmit = () => {
		if (!allowSubmit) return;
		if (allowConfirmation) {
			handleConfirmOnOpen();
			return;
		}
		orderCreate();
	};

	return (
		<Dialog open maxWidth="xl" onClose={onDialogClose} fullWidth scroll="body">
			<DialogTitle>{t("osfc.dialogs.orders-import-preview.title")}</DialogTitle>
			<DialogContent>
				<PreviewList data={ordersWithWarningsData} extraProps={extraProps} />
			</DialogContent>
			<DialogActions>
				<Button
					variant="contained"
					disabled={createLoading}
					label={t("ra.action.cancel")}
					onClick={onDialogClose}
					color="secondary"
				/>
				<Button
					variant="contained"
					label={t("osfc.buttons.submit")}
					onClick={handleOnSubmit}
					color="primary"
					disabled={submitDisabled}
					startIcon={
						createLoading ? (
							<CircularProgress size={16} thickness={5} color="inherit" />
						) : (
							<SubmitIcon />
						)
					}
				/>
				{allowConfirmation && (
					<Confirm
						isOpen={submitConfirmOpen}
						content="osfc.dialogs.orders-import-preview.dialogs.confirm.content"
						title="osfc.dialogs.orders-import-preview.dialogs.confirm.title"
						onConfirm={handleOnSubmitConfirm}
						onClose={handleConfirmOnClose}
					/>
				)}
			</DialogActions>
		</Dialog>
	);
};

const margeData = (left, right) => {
	const mainData = { ...left };
	const mergeData = { ...right };
	Object.keys(mergeData).forEach((key) => {
		if (mainData.hasOwnProperty(key)) {
			mainData[key] = { ...mainData[key], ...mergeData[key] };
		}
	});
	return mainData;
};

export const PreviewListActions = (props) => {
	const { onEdit, isEdit, extraProps = {}, ...rest } = props;

	const translate = useTranslate();
	const notifySuccess = useNotifySuccess();
	const notifyError = useNotifyError();
	const [validateLoading, setValidateLoading] = useState(false);

	if (!props.defaultData && !isEmptyObject(props.data)) {
		props.setDefaultData(structuredClone(props.data));
	}

	const orderValidate = () => {
		const _data = {
			order_import_id: extraProps.order_import_id,
			mapper_type: extraProps.mapper_type,
			order_items: Object.values(margeData(props.data, props.selectedFields)),
			company_id: extraProps.company_id,
			split_orders_type: extraProps.split_orders_type,
			add_loading_sequence_to_ref: extraProps.add_loading_sequence_to_ref,
			attach_source_doc: extraProps.attach_source_doc,
			split_document_content: extraProps.split_document_content,
			should_generate_pallets: extraProps.should_generate_pallets,
		};
		setValidateLoading(true);
		httpClient("orders-upload-validate", { method: "POST", body: JSON.stringify(_data) })
			.then(({ json }) => {
				notifySuccess(translate("ra.notification.updated", { smart_count: 1 }), {
					type: "info",
					messageArgs: { smart_count: 1 },
				});
				props.cancelEdit();
				extraProps?.setSharedDataPreviewData?.(json.order_items);
				props.setDefaultData(null);
			})
			.catch((error) => {
				notifyError(error);
			})
			.finally(() => {
				setValidateLoading(false);
			});
	};

	const onCancel = () => {
		props.cancelEdit();
		Object.assign(props.data, props.defaultData);
		props.setDefaultData(null);
	};

	const applyDisabled = validateLoading || isEmptyObject(props.selectedFields);

	return (
		<TopToolbar {...sanitizeListRestProps(rest)}>
			{!isEdit ? (
				<ToolbarButton onClick={onEdit} disabled={extraProps?.isExistWarnings === false}>
					{translate("osfc.buttons.edit")}
				</ToolbarButton>
			) : null}
			{isEdit ? (
				<ToolbarButton
					onClick={orderValidate}
					disabled={applyDisabled}
					icon={
						validateLoading ? (
							<CircularProgress size={16} thickness={5} color="inherit" />
						) : (
							<SubmitIcon />
						)
					}
				>
					{translate("osfc.buttons.apply_changes")}
				</ToolbarButton>
			) : null}
			{isEdit ? (
				<ToolbarButton onClick={onCancel} disabled={validateLoading}>
					{translate("osfc.buttons.cancel")}
				</ToolbarButton>
			) : null}
		</TopToolbar>
	);
};

export const PreviewList = ({ data, extraProps = {} }) => {
	const [ids, setIds] = React.useState();
	const [dicData, setDicData] = React.useState();
	const [selectedIds, setSelectedIds] = React.useState([]);
	const [selectedFields, setSelectedFields] = React.useState({});
	const [defaultData, setDefaultData] = React.useState(null);
	const [isEdit, setIsEdit] = React.useState(false);

	const visible = dicData && ids;

	useEffect(() => {
		if (data) {
			const _data = data.map((d, index) => ({ ...d, id: index + 1 }));
			const _ids = _data.map((x) => x["id"]);
			setIds(_ids);
			const _dicData = keyBy(_data, "id");
			setDicData(_dicData);
		} else {
			setIds(undefined);
			setDicData(undefined);
		}
	}, [data]);

	const listValue = { ids: ids, data: dicData, currentSort: { field: "id", order: "ASC" } };

	const onEdit = () => {
		setIsEdit(true);
		extraProps?.setSubmitDisabled?.(true);
	};

	const updateSelectedIds = (newId) => {
		setSelectedIds((prevSelectedIds) => {
			if (prevSelectedIds.includes(newId)) {
				return prevSelectedIds;
			}
			return [...prevSelectedIds, newId];
		});
	};

	const updateSelectedFields = (newField, source) => {
		setSelectedFields((prevFields) => {
			prevFields[newField.id] = { ...prevFields[newField.id], [source]: newField[source] };
			return prevFields;
		});
	};

	const cancelEdit = () => {
		setSelectedIds([]);
		setSelectedFields({});
		setIsEdit(false);
		extraProps?.setSubmitDisabled?.(false);
	};

	const sharedFieldsProps = {
		editableFieldProps: {
			editable: isEdit,
			selectedIds,
			updateSelectedIds,
			selectedFields,
			updateSelectedFields,
		},
	};

	return visible ? (
		<PreviewListDataGrid
			value={listValue}
			isEdit={isEdit}
			sharedFieldsProps={sharedFieldsProps}
			Actions={
				<PreviewListActions
					data={dicData}
					selectedIds={selectedIds}
					cancelEdit={cancelEdit}
					selectedFields={selectedFields}
					onEdit={onEdit}
					isEdit={isEdit}
					defaultData={defaultData}
					setDefaultData={setDefaultData}
					extraProps={extraProps}
				/>
			}
		/>
	) : (
		<></>
	);
};

const editableStyles = { border: "3px dotted", minHeight: "20px" };
const editableFieldColor = "gray";

const TextEditableField = (props) => (
	<EditableGridCellField {...props} style={editableStyles} changesFieldColor={editableFieldColor} />
);

const ReferenceEditableField = (props) => {
	const { inputFieldProps = {}, fieldFieldProps = {}, ...rest } = props;

	const EditInputComponent = (inputProps) => {
		const { renderFieldProps, ...restInputFieldProps } = inputFieldProps;

		return (
			<ReferenceInput {...inputProps} {...restInputFieldProps}>
				<SelectInput {...renderFieldProps} />
			</ReferenceInput>
		);
	};

	const EditFieldComponent = (fieldProps) => {
		const { renderFieldProps, optimized = false, ...restFieldFieldProps } = fieldFieldProps;

		if (optimized) {
			return (
				<div {...fieldProps} {...restFieldFieldProps}>
					<TextField {...renderFieldProps} />
				</div>
			);
		}

		return (
			<ReferenceField {...fieldProps} {...restFieldFieldProps}>
				<TextField {...renderFieldProps} />
			</ReferenceField>
		);
	};

	return (
		<TextEditableField
			{...rest}
			EditInputComponent={EditInputComponent}
			EditFieldComponent={EditFieldComponent}
		/>
	);
};

const ReferenceTextEditableField = (props) => {
	const { commonFieldProps = {}, ...rest } = props;
	const { mainSource, referenceSource } = commonFieldProps;

	if (rest.source === mainSource) {
		return <TextEditableField {...rest} />;
	} else if (rest.source === referenceSource) {
		return <ReferenceEditableField {...rest} />;
	}

	return null;
};

const DateEditableField = (props) => (
	<TextEditableField
		{...props}
		EditInputComponent={DateInput}
		EditFieldComponent={DateFormatField}
	/>
);

const TimeEditableField = (props) => (
	<TextEditableField
		{...props}
		EditInputComponent={TimeInput}
		EditFieldComponent={TimeFormatField}
	/>
);

const DateTimeEditableField = (props) => (
	<TextEditableField
		{...props}
		EditInputComponent={DateTimeInput}
		EditFieldComponent={DateTimeFormatField}
	/>
);

const YesNoEditableField = (props) => (
	<TextEditableField {...props} EditInputComponent={YesNoInput} EditFieldComponent={YesNoField} />
);

const NumberEditableField = (props) => (
	<TextEditableField {...props} EditInputComponent={NumberInput} EditFieldComponent={NumberField} />
);

const SelectEnumEditableField = (props) => (
	<TextEditableField
		{...props}
		EditInputComponent={SelectEnumInput}
		EditFieldComponent={SelectEnumField}
	/>
);

const ServiceEditableField = (props) => {
	const { data: terminals } = useTerminalsContext();

	const choices = React.useMemo(
		() =>
			getOrderServiceActiveChoices(
				props.record.terminal_id,
				props.record.service,
				Object.values(terminals)
			),
		[props.record.terminal_id, props.record.service, terminals]
	);

	return (
		<TextEditableField
			{...props}
			EditInputComponent={SelectInput}
			EditFieldComponent={SelectField}
			allowEmpty={true}
			choices={choices}
		/>
	);
};

const PhoneEditableField = (props) => (
	<TextEditableField {...props} EditInputComponent={PhoneInput} EditFieldComponent={PhoneField} />
);

const PreviewListDataGrid = (props) => {
	const dataValues = Object.values(props.value.data || {});
	const sampleRecord = dataValues.length > 0 ? dataValues[0] : null;
	const { isFieldHidden } = useTerminalFieldVisibility(sampleRecord?.terminal_id);
	const customFieldColumns = React.useMemo(() => {
		if (!sampleRecord?.custom_fields) return [];

		return sampleRecord.custom_fields.map((customField) => (
			<CustomFieldWarningColumn
				source={`custom_fields.${customField.key}`}
				label={customField.name}
				key={`custom_${customField.key}`}
				customField={customField}
			/>
		));
	}, [sampleRecord]);

	return (
		<WarningList {...props}>
			<WarningField
				component={FakeReferenceTextField}
				editableComponent={ReferenceEditableField}
				editableComponentsProps={{
					reference: "customer-companies",
					allowEmpty: true,
					inputFieldProps: {
						renderFieldProps: { optionText: "full_name" },
						filter: { no_range: true, is_blocked: false, is_deleted: false },
					},
					fieldFieldProps: {
						renderFieldProps: { source: "full_name" },
					},
				}}
				source="company_id"
				label="resources.orders.fields.company_id"
				replaceSource="customer_name"
			/>

			<WarningField
				component={YesNoField}
				editableComponent={YesNoEditableField}
				source="priority"
				label="resources.orders.fields.priority"
			/>
			<WarningField
				component={TextField}
				editableComponent={TextEditableField}
				source="reference"
				label="resources.orders.fields.reference"
			/>

			<WarningField
				component={FakeReferenceTextField}
				editableComponent={ServiceEditableField}
				replaceSource="service_name"
				source="service"
				label="resources.orders.fields.service"
			/>

			<WarningField
				component={FakeReferenceTextField}
				editableComponent={ReferenceEditableField}
				editableComponentsProps={{
					reference: "terminal",
					inputFieldProps: {
						renderFieldProps: { optionText: "name" },
					},
					fieldFieldProps: {
						renderFieldProps: { source: "name" },
					},
				}}
				source="terminal_id"
				label="resources.orders.fields.terminal"
				replaceSource="terminal_name"
			/>

			<WarningField
				component={TextField}
				editableComponent={DateEditableField}
				source="eta_date"
				label="resources.orders.fields.eta_date"
			/>
			<WarningField
				component={TextField}
				editableComponent={TimeEditableField}
				source="eta_time"
				label="resources.orders.fields.eta_time"
			/>
			{/* <WarningField
			component={TextField}
			editableComponent={TimeEditableField}
			source="eta_slot_time"
			label="resources.orders.fields.eta_slot_time"
		/> */}

			{!isFieldHidden(HideableOrderField.ETA_3RD_PARTY) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceEditableField}
					editableComponentsProps={{
						reference: "transporters",
						inputFieldProps: {
							renderFieldProps: { optionText: "full_name" },
							filter: {
								no_range: true,
								is_blocked: false,
								is_deleted: false,
								all_transporters: true,
							},
						},
						fieldFieldProps: {
							renderFieldProps: { source: "full_name" },
						},
					}}
					source="eta_3rd_party"
					label="resources.orders.fields.eta_3rd_party"
					replaceSource="eta_3rd_party_name"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.ETA_SHOW_DOC_FOR_TRANS) ? (
				<WarningField
					component={YesNoField}
					editableComponent={YesNoEditableField}
					source="eta_show_doc_for_trans"
					label="resources.orders.fields.eta_show_doc"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.ETA_DRIVER) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceTextEditableField}
					editableComponentsProps={{
						reference: "drivers",
						allowEmpty: true,
						inputFieldProps: {
							renderFieldProps: { optionText: (rec) => rec.first_name + " " + rec.last_name },
							filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
							format: (value) => (value === 0 ? "" : value),
							parse: (value) => (value === "" ? 0 : value),
						},
						fieldFieldProps: {
							renderFieldProps: { source: "first_name" },
						},
						commonFieldProps: {
							mainSource: "eta_driver",
							referenceSource: "eta_driver_id",
						},
					}}
					allowReplaceSource={allowReplaceSource}
					source="eta_driver|eta_driver_id"
					label="resources.orders.fields.eta_driver"
					replaceSource="eta_driver_name"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.ETA_DRIVER_PHONE) ? (
				<WarningField
					component={PhoneField}
					editableComponent={PhoneEditableField}
					source="eta_driver_phone"
					label="resources.orders.fields.eta_driver_phone"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.ETA_TRUCK) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceTextEditableField}
					editableComponentsProps={{
						reference: "trucks",
						allowEmpty: true,
						inputFieldProps: {
							renderFieldProps: { optionText: "name" },
							filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
						},
						fieldFieldProps: {
							renderFieldProps: { source: "name" },
						},
						commonFieldProps: {
							mainSource: "eta_truck",
							referenceSource: "eta_truck_id",
						},
					}}
					allowReplaceSource={allowReplaceSource}
					source="eta_truck|eta_truck_id"
					label="resources.orders.fields.eta_truck"
					replaceSource="eta_truck_name"
				/>
			) : null}
			{!isFieldHidden(HideableOrderField.ETA_TRAILER) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceTextEditableField}
					editableComponentsProps={{
						reference: "trailers",
						allowEmpty: true,
						inputFieldProps: {
							renderFieldProps: { optionText: "name" },
							filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
						},
						fieldFieldProps: {
							renderFieldProps: { source: "name" },
						},
						commonFieldProps: {
							mainSource: "eta_trailer",
							referenceSource: "eta_trailer_id",
						},
					}}
					allowReplaceSource={allowReplaceSource}
					source="eta_trailer|eta_trailer_id"
					label="resources.orders.fields.eta_trailer"
					replaceSource="eta_trailer_name"
				/>
			) : null}

			{/* <WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="place_from"
			label="resources.orders.fields.place_from"
		/> */}
			{/* <WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="is_eta_3rd_party_reload"
			label="resources.orders.fields.is_eta_3rd_party_reload"
		/> */}

			{/* <WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "company-users",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: (rec) => rec.first_name + " " + rec.last_name },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
					format: (value) => (value === 0 ? "" : value),
					parse: (value) => (value === "" ? 0 : value),
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="assignee_1"
			label="resources.orders.fields.assignee_1"
			replaceSource="assignee_1_name"
		/> */}

			<WarningField
				component={TextField}
				editableComponent={DateEditableField}
				source="etd_date"
				label="resources.orders.fields.etd_date"
			/>
			<WarningField
				component={TextField}
				editableComponent={TimeEditableField}
				source="etd_time"
				label="resources.orders.fields.etd_time"
			/>
			{/* <WarningField
			component={TextField}
			editableComponent={TimeEditableField}
			source="etd_slot_time"
			label="resources.orders.fields.etd_slot_time"
		/> */}

			{!isFieldHidden(HideableOrderField.ETD_3RD_PARTY) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceEditableField}
					editableComponentsProps={{
						reference: "transporters",
						inputFieldProps: {
							renderFieldProps: { optionText: "full_name" },
							filter: {
								no_range: true,
								is_blocked: false,
								is_deleted: false,
								all_transporters: true,
							},
						},
						fieldFieldProps: {
							renderFieldProps: { source: "full_name" },
						},
					}}
					source="etd_3rd_party"
					label="resources.orders.fields.etd_3rd_party"
					replaceSource="etd_3rd_party_name"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.ETD_SHOW_DOC_FOR_TRANS) ? (
				<WarningField
					component={YesNoField}
					editableComponent={YesNoEditableField}
					source="etd_show_doc_for_trans"
					label="resources.orders.fields.etd_show_doc"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.ETD_DRIVER) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceTextEditableField}
					editableComponentsProps={{
						reference: "drivers",
						allowEmpty: true,
						inputFieldProps: {
							renderFieldProps: { optionText: (rec) => rec.first_name + " " + rec.last_name },
							filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
							format: (value) => (value === 0 ? "" : value),
							parse: (value) => (value === "" ? 0 : value),
						},
						fieldFieldProps: {
							renderFieldProps: { source: "first_name" },
						},
						commonFieldProps: {
							mainSource: "etd_driver",
							referenceSource: "etd_driver_id",
						},
					}}
					allowReplaceSource={allowReplaceSource}
					source="etd_driver|etd_driver_id"
					label="resources.orders.fields.etd_driver"
					replaceSource="etd_driver_name"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.ETD_DRIVER_PHONE) ? (
				<WarningField
					component={PhoneField}
					editableComponent={PhoneEditableField}
					source="etd_driver_phone"
					label="resources.orders.fields.etd_driver_phone"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.ETD_TRUCK) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceTextEditableField}
					editableComponentsProps={{
						reference: "trucks",
						allowEmpty: true,
						inputFieldProps: {
							renderFieldProps: { optionText: "name" },
							filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
						},
						fieldFieldProps: {
							renderFieldProps: { source: "name" },
						},
						commonFieldProps: {
							mainSource: "etd_truck",
							referenceSource: "etd_truck_id",
						},
					}}
					allowReplaceSource={allowReplaceSource}
					source="etd_truck|etd_truck_id"
					label="resources.orders.fields.etd_truck"
					replaceSource="etd_truck_name"
				/>
			) : null}
			{!isFieldHidden(HideableOrderField.ETD_TRAILER) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceTextEditableField}
					editableComponentsProps={{
						reference: "trailers",
						allowEmpty: true,
						inputFieldProps: {
							renderFieldProps: { optionText: "name" },
							filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
						},
						fieldFieldProps: {
							renderFieldProps: { source: "name" },
						},
						commonFieldProps: {
							mainSource: "etd_trailer",
							referenceSource: "etd_trailer_id",
						},
					}}
					allowReplaceSource={allowReplaceSource}
					source="etd_trailer|etd_trailer_id"
					label="resources.orders.fields.etd_trailer"
					replaceSource="etd_trailer_name"
				/>
			) : null}

			{/* <WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="place_to"
			label="resources.orders.fields.place_to"
		/> */}
			{/* <WarningField
			component={YesNoField}
			editableComponent={YesNoEditableField}
			source="is_etd_3rd_party_reload"
			label="resources.orders.fields.is_etd_3rd_party_reload"
		/> */}

			{/* <WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "company-users",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: (rec) => rec.first_name + " " + rec.last_name },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
					format: (value) => (value === 0 ? "" : value),
					parse: (value) => (value === "" ? 0 : value),
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="assignee_2"
			label="resources.orders.fields.assignee_2"
			replaceSource="assignee_2_name"
		/> */}
			{/* <WarningField
			component={FakeReferenceTextField}
			editableComponent={ReferenceEditableField}
			editableComponentsProps={{
				reference: "company-users",
				allowEmpty: true,
				inputFieldProps: {
					renderFieldProps: { optionText: (rec) => rec.first_name + " " + rec.last_name },
					filter: { no_range: true, is_terminal: true, is_blocked: false, is_deleted: false },
					format: (value) => (value === 0 ? "" : value),
					parse: (value) => (value === "" ? 0 : value),
				},
				fieldFieldProps: {
					renderFieldProps: { source: "name" },
				},
			}}
			source="assignee_3"
			label="resources.orders.fields.assignee_3"
			replaceSource="assignee_3_name"
		/> */}

			{!isFieldHidden(HideableOrderField.BOXES) ? (
				<WarningField
					component={TextField}
					editableComponent={NumberEditableField}
					source="boxes"
					label="resources.orders.fields.boxes"
				/>
			) : null}
			{/* <WarningField
			component={TextField}
			editableComponent={NumberEditableField}
			source="label"
			label="resources.orders.fields.label"
		/> */}

			{/* <WarningField
			component={FakeReferenceTextField}
			editableComponent={SelectEnumEditableField}
			editableComponentsProps={{
				enumName: "label_source",
				enumObj: LabelSource,
			}}
			replaceSource="label_source_name"
			source="label_source"
			label="resources.orders.fields.label_source"
		/> */}

			{!isFieldHidden(HideableOrderField.PALLETS) ? (
				<WarningField
					component={TextField}
					editableComponent={NumberEditableField}
					source="pallets"
					label="resources.orders.fields.pallets"
				/>
			) : null}
			{!isFieldHidden(HideableOrderField.KILOS) ? (
				<WarningField
					component={TextField}
					editableComponent={NumberEditableField}
					source="kilos"
					label="resources.orders.fields.kilos"
				/>
			) : null}
			{!isFieldHidden(HideableOrderField.IS_DOC_FOR_NFSA) ? (
				<WarningField
					component={YesNoField}
					editableComponent={YesNoEditableField}
					source="is_doc_for_nfsa"
					label="resources.orders.fields.is_doc_for_nfsa"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.COMMODITY_ID) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceEditableField}
					editableComponentsProps={{
						reference: "commodities",
						allowEmpty: true,
						inputFieldProps: {
							renderFieldProps: { optionText: "name" },
							filter: { no_range: true, is_deleted: false },
						},
						fieldFieldProps: {
							renderFieldProps: { source: "name" },
						},
					}}
					source="commodity_id"
					label="resources.orders.fields.commodity_id"
					replaceSource="commodity_name"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.COOLER_TYPE_ID) ? (
				<WarningField
					component={FakeReferenceTextField}
					editableComponent={ReferenceEditableField}
					editableComponentsProps={{
						reference: "cooler-types",
						allowEmpty: true,
						inputFieldProps: {
							renderFieldProps: { optionText: "name" },
						},
						fieldFieldProps: {
							renderFieldProps: { source: "name" },
						},
					}}
					source="cooler_type_id"
					label="resources.orders.fields.cooler_type_id"
					replaceSource="cooler_type_name"
				/>
			) : null}

			{!isFieldHidden(HideableOrderField.SPLIT_LOAD) ? (
				<WarningField
					component={YesNoField}
					editableComponent={YesNoEditableField}
					source="split_load"
					label="resources.orders.fields.split_load"
				/>
			) : null}
			{!isFieldHidden("outgoing_trip_number") ? (
				<WarningField
					component={TextField}
					editableComponent={TextEditableField}
					source="outgoing_trip_number"
					label="resources.orders.fields.outgoing_trip_number"
				/>
			) : null}
			{!isFieldHidden("loading_sequence") ? (
				<WarningField
					component={TextField}
					editableComponent={NumberEditableField}
					source="loading_sequence"
					label="resources.orders.fields.loading_sequence"
				/>
			) : null}
			{!isFieldHidden(HideableOrderField.PLUMB) ? (
				<WarningField
					component={YesNoField}
					editableComponent={YesNoEditableField}
					source="plumb"
					label="resources.orders.fields.plumb"
				/>
			) : null}
			{!isFieldHidden(HideableOrderField.SEAL) ? (
				<WarningField
					component={YesNoField}
					editableComponent={YesNoEditableField}
					source="seal"
					label="resources.orders.fields.seal"
				/>
			) : null}
			{!isFieldHidden(HideableOrderField.PRINTED_DOC) ? (
				<WarningField
					component={TextField}
					editableComponent={TextEditableField}
					source="printed_doc"
					label="resources.orders.fields.printed_doc"
				/>
			) : null}
			{!isFieldHidden("is_extra_loading_time_billable") ? (
				<WarningField
					component={YesNoField}
					editableComponent={YesNoEditableField}
					source="is_extra_loading_time_billable"
					label="resources.orders.fields.is_extra_loading_time_billable"
				/>
			) : null}
			{!envSettings.REACT_APP_FF_STORAGE_MODULE && !isFieldHidden(HideableOrderField.IS_AREA_BLOCKED) ? (
				<WarningField
					component={YesNoField}
					editableComponent={YesNoEditableField}
					source="is_area_blocked"
					label="resources.orders.fields.is_area_blocked"
				/>
			) : null}
			<WarningField
				component={YesNoField}
				editableComponent={YesNoEditableField}
				source="set_order_completed"
				label="resources.orders.fields.set_order_completed"
			/>
			{/* <WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="notes"
			label="resources.orders.fields.notes"
			maxWidth={100}
		/>
		<WarningField
			component={TextField}
			editableComponent={TextEditableField}
			source="internal_notes"
			label="resources.orders.fields.internal_notes"
			maxWidth={100}
		/> */}
			{customFieldColumns}
		</WarningList>
	);
};

export { PreviewDialog as default };
